import { useState, useCallback, useMemo } from "react";
import {
  Database,
  Download,
  Play,
  RotateCcw,
  CheckCircle,
  XCircle,
  Clock,
  HardDrive,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import {
  useBackup,
  useTriggerBackup,
  useUpdateBackupSchedule,
} from "@/hooks/admin/useEnterprise";
import type {
  BackupJob,
  BackupJobStatus,
  BackupJobType,
  BackupSchedule,
} from "@/lib/admin-api/enterprise";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPE_LABELS: Record<BackupJobType, string> = {
  full: "Complete",
  incremental: "Incrementielle",
  schema_only: "Schema uniquement",
};

const TYPE_COLORS: Record<BackupJobType, string> = {
  full: "border-emerald-200 bg-emerald-50 text-emerald-700",
  incremental: "border-blue-200 bg-blue-50 text-blue-700",
  schema_only: "border-stone-200 bg-stone-50 text-stone-700",
};

const STATUS_LABELS: Record<BackupJobStatus, string> = {
  scheduled: "Planifiee",
  running: "En cours",
  completed: "Terminee",
  failed: "Echouee",
};

const STATUS_COLORS: Record<BackupJobStatus, string> = {
  scheduled: "bg-stone-100 text-stone-600 border-stone-200",
  running: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_ICONS: Record<BackupJobStatus, typeof CheckCircle> = {
  scheduled: Clock,
  running: RotateCcw,
  completed: CheckCircle,
  failed: XCircle,
};

const RETENTION_PRESETS: readonly { readonly value: number; readonly label: string }[] = [
  { value: 7, label: "7 jours" },
  { value: 30, label: "30 jours" },
  { value: 90, label: "90 jours" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateTime(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(start: string, end: string | null): string {
  if (!end) return "\u2014";
  const durationMs = new Date(end).getTime() - new Date(start).getTime();
  if (durationMs < 1000) return `${durationMs} ms`;
  const seconds = Math.floor(durationMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatSize(mb: number | null): string {
  if (mb == null) return "\u2014";
  if (mb < 1) return `${Math.round(mb * 1024)} Ko`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} Go`;
  return `${mb.toFixed(1)} Mo`;
}

// ---------------------------------------------------------------------------
// Backup Type Selector
// ---------------------------------------------------------------------------

interface BackupTypeSelectorProps {
  readonly isPending: boolean;
  readonly onTrigger: (type: BackupJobType) => void;
}

function BackupTypeSelector({ isPending, onTrigger }: BackupTypeSelectorProps) {
  const types: readonly { readonly type: BackupJobType; readonly desc: string }[] = [
    { type: "full", desc: "Sauvegarde complete de la base de donnees" },
    { type: "incremental", desc: "Modifications depuis la derniere sauvegarde" },
    { type: "schema_only", desc: "Structure des tables uniquement" },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {types.map(({ type, desc }) => (
        <button
          key={type}
          onClick={() => onTrigger(type)}
          disabled={isPending}
          className={`flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors hover:shadow-sm disabled:opacity-50 ${TYPE_COLORS[type]}`}
        >
          <div className="flex w-full items-center justify-between">
            <span className="text-sm font-semibold">{TYPE_LABELS[type]}</span>
            <Play className="h-4 w-4" />
          </div>
          <p className="text-xs opacity-80">{desc}</p>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedule Config Card
// ---------------------------------------------------------------------------

interface ScheduleCardProps {
  readonly schedule: BackupSchedule;
  readonly isSaving: boolean;
  readonly onSave: (s: Partial<BackupSchedule>) => Promise<void>;
}

function ScheduleCard({ schedule, isSaving, onSave }: ScheduleCardProps) {
  const [freq, setFreq] = useState(schedule.frequency);
  const [time, setTime] = useState(schedule.time);
  const [retention, setRetention] = useState(schedule.retention_days);
  const [enabled, setEnabled] = useState(schedule.enabled);
  const [error, setError] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    setError(null);
    try {
      await onSave({
        frequency: freq,
        time,
        retention_days: retention,
        enabled,
      });
      toast.success("Configuration de planification enregistree");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Une erreur est survenue.";
      setError(message);
      toast.error("Echec de la sauvegarde de la configuration");
    }
  }, [freq, time, retention, enabled, onSave]);

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-stone-900">
          Configuration de la planification
        </h3>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${enabled ? "text-emerald-600" : "text-stone-400"}`}>
            {enabled ? "Active" : "Desactivee"}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => setEnabled(!enabled)}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
              enabled ? "bg-amber-600" : "bg-stone-200"
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                enabled ? "translate-x-4" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 rounded-md bg-red-50 px-3 py-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {/* Frequency */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-stone-600">Frequence</label>
          <select
            value={freq}
            onChange={(e) => setFreq(e.target.value as BackupSchedule["frequency"])}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="daily">Quotidienne</option>
            <option value="weekly">Hebdomadaire</option>
            <option value="monthly">Mensuelle</option>
          </select>
        </div>

        {/* Time */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-stone-600">Heure</label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* Retention */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-stone-600">
            Retention
          </label>
          <div className="flex gap-1.5">
            {RETENTION_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => setRetention(preset.value)}
                className={`flex-1 rounded-md border px-2 py-2 text-xs font-medium transition-colors ${
                  retention === preset.value
                    ? "border-amber-300 bg-amber-50 text-amber-900"
                    : "border-stone-200 text-stone-600 hover:bg-stone-50"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Save */}
        <div className="flex items-end">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full"
          >
            {isSaving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Progress indicator
// ---------------------------------------------------------------------------

function BackupProgress() {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
      <div>
        <p className="text-sm font-medium text-blue-900">Sauvegarde en cours...</p>
        <p className="text-xs text-blue-700">Veuillez patienter, cela peut prendre quelques minutes.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Backup() {
  useAdminPageTitle("Sauvegardes");

  const { data, isLoading, isError } = useBackup();
  const triggerMutation = useTriggerBackup();
  const updateScheduleMutation = useUpdateBackupSchedule();

  const jobs = data?.jobs ?? [];
  const schedule = data?.schedule;
  const pagination = data?.pagination;

  const completedCount = useMemo(
    () => jobs.filter((j) => j.status === "completed").length,
    [jobs],
  );
  const failedCount = useMemo(
    () => jobs.filter((j) => j.status === "failed").length,
    [jobs],
  );
  const totalSizeMb = useMemo(
    () => jobs.reduce((sum, j) => sum + (j.size_mb ?? 0), 0),
    [jobs],
  );
  const lastCompleted = useMemo(
    () =>
      jobs
        .filter((j) => j.status === "completed" && j.completed_at)
        .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime())[0],
    [jobs],
  );

  const isRunning = useMemo(
    () => jobs.some((j) => j.status === "running") || triggerMutation.isPending,
    [jobs, triggerMutation.isPending],
  );

  const handleTrigger = useCallback(
    async (type: BackupJobType) => {
      try {
        await triggerMutation.mutateAsync(type);
        toast.success(`Sauvegarde ${TYPE_LABELS[type].toLowerCase()} lancee`);
      } catch {
        toast.error("Echec du lancement de la sauvegarde");
      }
    },
    [triggerMutation],
  );

  const handleScheduleSave = useCallback(
    async (s: Partial<BackupSchedule>) => {
      await updateScheduleMutation.mutateAsync(s);
    },
    [updateScheduleMutation],
  );

  const handleDownload = useCallback((url: string) => {
    toast.success("Telechargement lance");
    window.open(url, "_blank");
  }, []);

  const columns: readonly ColumnDef<BackupJob>[] = [
    {
      id: "type",
      header: "Type",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${TYPE_COLORS[row.type]}`}
        >
          {TYPE_LABELS[row.type]}
        </span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => {
        const Icon = STATUS_ICONS[row.status];
        return (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[row.status]}`}
          >
            <Icon className={`h-3 w-3 ${row.status === "running" ? "animate-spin" : ""}`} />
            {STATUS_LABELS[row.status]}
          </span>
        );
      },
    },
    {
      id: "size_mb",
      header: "Taille",
      cell: (row) => (
        <span className="text-sm font-medium text-stone-700">
          {formatSize(row.size_mb)}
        </span>
      ),
    },
    {
      id: "duration",
      header: "Duree",
      cell: (row) => (
        <span className="text-xs text-stone-500">
          {formatDuration(row.created_at, row.completed_at)}
        </span>
      ),
    },
    {
      id: "created_at",
      header: "Cree le",
      cell: (row) => (
        <span className="text-xs text-stone-500">{formatDateTime(row.created_at)}</span>
      ),
    },
    {
      id: "retention_days",
      header: "Retention",
      cell: (row) => (
        <span className="text-xs text-stone-500">{row.retention_days} jours</span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row) => (
        <div className="flex items-center gap-1.5">
          {row.status === "completed" && row.download_url && (
            <button
              onClick={() => handleDownload(row.download_url!)}
              className="flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
            >
              <Download className="h-3 w-3" />
              Telecharger
            </button>
          )}
          {row.status === "failed" && row.error && (
            <span
              className="max-w-xs truncate rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700"
              title={row.error}
            >
              {row.error}
            </span>
          )}
        </div>
      ),
    },
  ];

  if (isError) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <ShieldAlert className="h-10 w-10 text-red-400" />
          <p className="mt-3 text-sm font-medium text-stone-700">
            Erreur lors du chargement des sauvegardes
          </p>
          <p className="mt-1 text-xs text-stone-500">
            Veuillez reessayer ou contacter le support.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <Database className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Sauvegardes</h1>
            <p className="text-sm text-stone-500">
              Gestion des sauvegardes automatiques et manuelles
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Sauvegardes reussies"
            value={completedCount}
            icon={<CheckCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Echecs"
            value={failedCount}
            icon={<XCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Stockage total"
            value={formatSize(totalSizeMb)}
            icon={<HardDrive className="h-4 w-4" />}
          />
          <KPICard
            label="Derniere sauvegarde"
            value={lastCompleted ? formatDateTime(lastCompleted.completed_at) : "\u2014"}
            icon={<Clock className="h-4 w-4" />}
          />
        </div>

        {/* Backup type selector */}
        <div>
          <h2 className="mb-3 text-sm font-semibold text-stone-700">
            Lancer une sauvegarde
          </h2>
          <BackupTypeSelector
            isPending={isRunning}
            onTrigger={handleTrigger}
          />
        </div>

        {/* Progress indicator */}
        {isRunning && <BackupProgress />}

        {/* Schedule config */}
        {schedule && (
          <ScheduleCard
            schedule={schedule}
            isSaving={updateScheduleMutation.isPending}
            onSave={handleScheduleSave}
          />
        )}

        {/* Recent backups */}
        <div>
          <h2 className="mb-3 text-sm font-semibold text-stone-700">
            Historique des sauvegardes
          </h2>
          <AdminDataTable
            columns={columns}
            data={jobs}
            pagination={
              pagination
                ? { page: pagination.page, limit: pagination.limit, total: pagination.total }
                : undefined
            }
            isLoading={isLoading}
            emptyMessage="Aucune sauvegarde trouvée."
          />
        </div>
      </div>
    </AdminLayout>
  );
}
